// How the dashboard works with the elements of a page: the distinct elements of a problem (one per
// selector, with the device each was found on), which of them can be located on a capture and why not,
// where to put the labels so they never hide each other, the region to enlarge around an element, a
// description of its position for people who do not see the capture, and the instructions to hand to
// whoever maintains the website. Pure functions, tested in element-logic.test.ts.
import type { Box, Device, ElementName, PageIssue, PageOccurrence } from './api';
import type { Problem } from './results-logic';

/** One element of a problem on a page, with what was found on each device. */
export interface PageElement {
  /** The id of its first occurrence: what the address carries to open it. */
  id: string;
  selector: string;
  name: ElementName;
  devices: Device[];
  byDevice: Partial<Record<Device, PageOccurrence>>;
  occurrenceIds: string[];
  /** Distance from the top of the page, to order them as they are read; null when not located. */
  top: number | null;
}

/**
 * The distinct elements of a problem on a page: the same element found on both devices is one element,
 * ordered top to bottom as the page is read, those that cannot be located last.
 */
export function elementsOf(problem: Problem<PageIssue>): PageElement[] {
  const bySelector = new Map<string, PageElement>();
  // A finding about the whole page (no element behind it) has nothing to select or to point at.
  for (const occurrence of problem.issues
    .flatMap((issue) => issue.occurrences)
    .filter((o) => o.selector || o.html)) {
    const key = occurrence.selector || occurrence.id;
    const found = bySelector.get(key);
    const top = occurrence.box ? occurrence.box.y : null;
    if (found) {
      found.byDevice[occurrence.device] ??= occurrence;
      if (!found.devices.includes(occurrence.device)) found.devices.push(occurrence.device);
      found.occurrenceIds.push(occurrence.id);
      if (found.top === null && top !== null) found.top = top;
    } else {
      bySelector.set(key, {
        id: occurrence.id,
        selector: occurrence.selector,
        name: occurrence.element,
        devices: [occurrence.device],
        byDevice: { [occurrence.device]: occurrence },
        occurrenceIds: [occurrence.id],
        top,
      });
    }
  }
  return [...bySelector.values()]
    .map((element) => ({
      ...element,
      devices: (['escritorio', 'movil'] as Device[]).filter((d) => element.devices.includes(d)),
    }))
    .sort(
      (a, b) =>
        (a.top ?? Number.POSITIVE_INFINITY) - (b.top ?? Number.POSITIVE_INFINITY) ||
        a.selector.localeCompare(b.selector),
    );
}

/** The element an id opens (any of its occurrences' ids), with the problem it belongs to. */
export function findElement(problems: Problem<PageIssue>[], id: string | undefined) {
  if (!id) return null;
  for (const problem of problems) {
    const elements = elementsOf(problem);
    const element = elements.find((candidate) => candidate.occurrenceIds.includes(id));
    if (element) return { problem, element, elements };
  }
  return null;
}

/** The device to show an element on: the one on screen when the element was found there, else where it was found. */
export const deviceFor = (element: PageElement, current: Device): Device =>
  element.devices.includes(current) ? current : (element.devices[0] ?? current);

export interface Capture {
  /** The scan still keeps this device's screenshot. */
  available: boolean;
  width: number;
  /** Known once the image has loaded. */
  height: number | null;
}

export type Locatable =
  | { ok: true; box: Box }
  | { ok: false; reason: 'no-capture' | 'no-position' | 'outside' };

/**
 * Whether an occurrence can be marked on the capture of its device, and, if it cannot, why: there is no
 * capture any more, the engine could not place it (inside a frame, not drawn) or it falls outside the
 * part of the page that was captured.
 */
export function locate(occurrence: PageOccurrence | undefined, capture: Capture): Locatable {
  if (!capture.available) return { ok: false, reason: 'no-capture' };
  const box = occurrence?.box;
  if (!box || box.width <= 0 || box.height <= 0) return { ok: false, reason: 'no-position' };
  if (capture.height !== null && box.y >= capture.height) return { ok: false, reason: 'outside' };
  if (box.x >= capture.width) return { ok: false, reason: 'outside' };
  return { ok: true, box };
}

export interface LabelRequest {
  id: string;
  box: Box;
  width: number;
  height: number;
  /**
   * How many label heights the label may be moved down from its place beside the element (0: only beside
   * it). A label is never put away from its element: it is left out instead.
   */
  shifts?: number;
}

const overlaps = (a: Box, b: Box) =>
  a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;

/**
 * Where to put each label (in page coordinates) so none covers another: beside its element on the first
 * free side (and, if the request allows it, moved down a little). The first request wins the best place
 * (the selected element goes first). A label that finds no free place next to its element is left out
 * (null): the element keeps its outline and its number in the list, and nothing is drawn away from its
 * element or on top of anything else.
 */
export function placeLabels(
  requests: LabelRequest[],
  bounds: { width: number; height: number },
): Map<string, { x: number; y: number } | null> {
  const placed: Box[] = [];
  const result = new Map<string, { x: number; y: number } | null>();
  for (const { id, box, width, height, shifts = 0 } of requests) {
    const sides = [
      { x: box.x, y: box.y - height },
      { x: box.x + box.width - width, y: box.y - height },
      { x: box.x, y: box.y + box.height },
      { x: box.x + box.width - width, y: box.y + box.height },
      { x: box.x, y: box.y },
    ];
    const spots = sides.flatMap((side) =>
      Array.from({ length: shifts + 1 }, (_, step) => ({ x: side.x, y: side.y + step * (height + 2) })),
    );
    const free = spots.find((spot) => {
      const label = { x: Math.max(0, spot.x), y: Math.max(0, spot.y), width, height };
      return (
        label.x + width <= bounds.width &&
        label.y + height <= bounds.height &&
        !placed.some((other) => overlaps(label, other))
      );
    });
    if (free) {
      const spot = { x: Math.max(0, free.x), y: Math.max(0, free.y) };
      placed.push({ ...spot, width, height });
      result.set(id, spot);
    } else result.set(id, null);
  }
  return result;
}

/**
 * The region of the capture to enlarge around an element: its box plus context, more of it at higher
 * `context`, kept inside the page and never too small to read what surrounds the element.
 */
export function cropRegion(box: Box, page: { width: number; height: number | null }, context = 1): Box {
  const padX = Math.max(box.width * 0.75, 200) * context;
  const padY = Math.max(box.height * 1.5, 120) * context;
  const height = page.height ?? box.y + box.height + padY;
  const x = Math.max(0, box.x - padX);
  const y = Math.max(0, box.y - padY);
  const right = Math.min(page.width, box.x + box.width + padX);
  const bottom = Math.min(height, box.y + box.height + padY);
  return { x, y, width: Math.max(1, right - x), height: Math.max(1, bottom - y) };
}

/** Roughly where an element is, in words a person can use without seeing the capture. */
export function positionOf(
  box: Box,
  page: { width: number; height: number | null },
): { vertical: number | null; side: 'left' | 'center' | 'right' } {
  const centreX = (box.x + box.width / 2) / page.width;
  return {
    vertical: page.height
      ? Math.min(100, Math.max(0, Math.round(((box.y + box.height / 2) / page.height) * 100)))
      : null,
    side: centreX < 1 / 3 ? 'left' : centreX > 2 / 3 ? 'right' : 'center',
  };
}
